import { MAX_LOOP_COUNT, StemType } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { applySnapshot, getSnapshot } from 'mobx-state-tree'
import { FC, useContext } from 'react'
import { useTreeState } from '../../../stores/Root'
import { ILoopRefStore } from '../../../stores/song/loop/LoopRefStore'
import { undoManager } from '../../../stores/StudioStore'
import { ClipboardContext } from '../Context'
import ContextMenu from '../controls/ContextMenu'
import { CopyTypes } from '../StudioConstants'
import { LoopContext } from './InstrumentLoop'

const useLoopContextMenu = (stemIndex: number, section: number, loopNumber: number) => {
  const { studio } = useTreeState()
  const { clipboard, setClipboard } = useContext(ClipboardContext)
  const stem = studio.song.stems[stemIndex]
  const currLoop = stem.getLoopAtSectionOrNull(section)
  const isDrums = stem.type() === StemType.DRUMS

  const copy = () => {
    setClipboard({
      type: isDrums ? CopyTypes.DRUMLOOP : CopyTypes.MELODICLOOP,
      value: getSnapshot(currLoop as any)
    })
  }

  const paste = (e, inNew = false) => {
    studio.disableAudioUpdates()
    if (
      clipboard.type !== (isDrums ? CopyTypes.DRUMLOOP : CopyTypes.MELODICLOOP) ||
      !clipboard.value
    )
      return

    undoManager.startGroup(() => {
      let loopToApplySnapshot = currLoop
      studio.disableAudioUpdates()
      if (currLoop === null || inNew) {
        studio.song.addLoopToStem(stemIndex)
        const loopCount = studio.song.stems[stemIndex].loops.length
        studio.song.updateLoopForStem(stemIndex, section, loopCount)
        loopToApplySnapshot = studio.song.stems[stemIndex].loops[loopCount - 1]
      }
      if (isDrums) {
        for (
          let i = 0;
          i < loopToApplySnapshot.drumLoop.subLoops.length &&
          i < (clipboard.value as any).drumLoop.subLoops.length;
          i++
        ) {
          applySnapshot(
            (loopToApplySnapshot as ILoopRefStore).drumLoop.subLoops[i],
            (clipboard.value as any).drumLoop.subLoops[i]
          )
        }
      } else applySnapshot(loopToApplySnapshot as any, clipboard.value)
      undoManager.stopGroup()
    })

    studio.enableAudioUpdates()
  }

  const randomize = () => {
    if (currLoop === null) studio.addNewLoopAndRandomize(stemIndex, section)
    else studio.randomizeLoop(stemIndex, loopNumber - 1)
  }

  const clearNotes = () => {
    studio.clearLoop(stemIndex, loopNumber - 1)
  }

  const loopSection = () => studio.loopSection(section)

  return { currLoop, copy, clipboard, isDrums, paste, stem, randomize, clearNotes, loopSection }
}

interface LoopContextMenuProps {}
const LoopContextMenu: FC<LoopContextMenuProps> = ({}) => {
  const { studio } = useTreeState()
  const { stemIndex, section, loopNumber, removeLoop } = useContext(LoopContext)
  const { currLoop, copy, clipboard, isDrums, paste, stem, randomize, clearNotes, loopSection } =
    useLoopContextMenu(stemIndex, section, loopNumber)

  if (!currLoop && studio.song.stems[stemIndex].isRemixed) return null
  return (
    <ContextMenu.Portal>
      <ContextMenu.Content>
        {currLoop && <ContextMenu.Item onClick={copy}>Copy Notes</ContextMenu.Item>}
        {/* Remixed stems are immutable in regards to the notes */}
        {!studio.song.stems[stemIndex].isRemixed && (
          <>
            {currLoop &&
              clipboard.value &&
              ((isDrums && clipboard.type === CopyTypes.DRUMLOOP) ||
                (!isDrums && clipboard.type === CopyTypes.MELODICLOOP)) && (
                <ContextMenu.Item onClick={paste}>Paste</ContextMenu.Item>
              )}
            {clipboard.value &&
              ((isDrums && clipboard.type === CopyTypes.DRUMLOOP) ||
                (!isDrums && clipboard.type === CopyTypes.MELODICLOOP)) &&
              stem.loops.length < MAX_LOOP_COUNT && (
                <ContextMenu.Item onClick={(e) => paste(e, true)}>Paste to New</ContextMenu.Item>
              )}
            {!(!currLoop && stem.loops.length >= MAX_LOOP_COUNT) && (
              <ContextMenu.Item onClick={randomize}>Randomize</ContextMenu.Item>
            )}
            {currLoop && <ContextMenu.Item onClick={clearNotes}>Clear Notes</ContextMenu.Item>}
          </>
        )}
        {currLoop && <ContextMenu.Item onClick={removeLoop}>Delete</ContextMenu.Item>}
        <ContextMenu.Item onClick={loopSection}>Loop Section</ContextMenu.Item>
      </ContextMenu.Content>
    </ContextMenu.Portal>
  )
}

export default observer(LoopContextMenu)
